import { useRef, useEffect } from 'react';
import { css } from '@emotion/react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import t from 'react-translate';

import { config } from '@config/pendo.config.json';
import ClickableContainer from 'components/clickable-container';
import NvIcon from 'shared/components/nv-icon';
import NvTooltip from 'shared/components/nv-tooltip';
import {
  gray3, gray6, primary, success, white, hexToRgbaString, black,
  gray7,
} from 'styles/global_defaults/colors';
import {
  doubleSpacing, halfSpacing, quarterSpacing, standardSpacing,
} from 'styles/global_defaults/scaffolding';

import { FlipCardData } from './flip-card';

type FlipCardPairRowProps = {
  card: FlipCardData,
  index: number,
  disableDelete: boolean,
  disableReorder: boolean,
  onChange: (field: 'front' | 'back', value: string) => void,
  onBlur?: () => void,
  onDelete: () => void,
};

const MAX_CHARS = 200;

const styles = css`
  &.pair-row {
    gap: ${halfSpacing}px;

    .pair-inputs {
      flex: 1;
    }

    .input-bg {
      background-color: ${gray7};
    }

    .drag-handle {
      flex: 0 0 auto;
      display: flex;
      align-items: center;
      color: ${gray3};
      cursor: grab;
      touch-action: none;
      visibility: hidden;

      &:hover, &:focus-visible {
        color: ${primary};
        visibility: visible;
      }

      &[aria-disabled='true'] {
        visibility: hidden;
        cursor: default;
      }
    }

    .pair-input {
      position: relative;
      flex: 1;
      border: 1px solid ${gray6};
      border-radius: ${quarterSpacing}px;
      box-shadow: 0px 4px 10px 0px ${hexToRgbaString(black, 0.1)};

      &:focus-within {
        border-color: ${primary};
      }

      textarea {
        display: block;
        width: 100%;
        text-align: center;
        border: none;
        min-height: 70px;
        box-shadow: none;
        resize: none;
        overflow: hidden;
        padding: ${standardSpacing}px ${standardSpacing}px ${quarterSpacing}px;
        background-color: ${white};
        font-size: inherit;
        font-family: inherit;
        line-height: inherit;
        color: inherit;
        border-radius: ${quarterSpacing}px;

        &::placeholder {
          color: ${gray3};
        }

        &:focus {
          outline: none;
        }
      }

      .char-count {
        align-self: flex-end;
        margin-top: auto;
        /*! @noflip */
        padding: 0 ${halfSpacing}px ${quarterSpacing}px;
      }
    }

    .connector {
      flex: 0 0 ${doubleSpacing}px;
      height: 2px;
      background-color: ${success};
    }

    .dot {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      width: ${standardSpacing}px;
      height: ${standardSpacing}px;
      border-radius: 50%;
      background-color: ${success};
      color: ${white};
      z-index: 4;
    }
    .dot-right {
      /*! @noflip */
      right: -${halfSpacing}px;
    }
    .dot-left {
      /*! @noflip */
      left: -${halfSpacing}px;
    }

    // Keep the delete button's space reserved so toggling it on hover/focus
    // doesn't reflow and resize the input boxes.
    .delete-btn {
      visibility: hidden;
    }

    &:hover, &:focus-within {
      .delete-btn {
        visibility: visible;
      }

      .drag-handle:not([aria-disabled='true']) {
        visibility: visible;
      }
    }
  }
`;

const FlipCardPairRow = (props: FlipCardPairRowProps) => {
  const {
    card, index, disableDelete, disableReorder, onChange, onBlur, onDelete,
  } = props;

  const {
    attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging,
  } = useSortable({ id: card.id, disabled: disableReorder });

  const frontRef = useRef<HTMLTextAreaElement>(null);
  const backRef = useRef<HTMLTextAreaElement>(null);

  const resizeTextarea = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  useEffect(() => { resizeTextarea(frontRef.current); }, [card.front]);
  useEffect(() => { resizeTextarea(backRef.current); }, [card.back]);

  const rowStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
    zIndex: isDragging ? 1 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={rowStyle}
      css={styles}
      className='pair-row d-flex align-items-center'
    >
      <NvTooltip text={t.LECTURE_PAGES.COMPONENTS.FLIP_CARD.EDIT.DRAG_AND_DROP_TO_REORDER()} enabled={!disableReorder}>
        <div
          ref={setActivatorNodeRef}
          className='drag-handle'
          aria-label={t.LECTURE_PAGES.COMPONENTS.FLIP_CARD.EDIT.REORDER_CARD()}
          {...attributes}
          {...listeners}
          aria-disabled={disableReorder}
          data-qa={config.pendo.flipCard.reorderCard}
          data-qa-id={`${config.pendo.flipCard.reorderCard}_${card.id}`}
        >
          <NvIcon icon='reorder' size='small' />
        </div>
      </NvTooltip>

      <div className='pair-inputs d-flex align-items-stretch'>
        <div className='pair-input d-flex flex-column'>
          <textarea
            ref={frontRef}
            value={card.front}
            maxLength={MAX_CHARS}
            onChange={(e) => onChange('front', e.target.value)}
            onBlur={onBlur}
            placeholder={t.FORM.TYPE_HERE()}
            aria-label={t.LECTURE_PAGES.COMPONENTS.FLIP_CARD.EDIT.FRONT()}
            data-qa={config.pendo.flipCard.editFront}
            data-qa-id={`${config.pendo.flipCard.editFront}_${card.id}`}
          />
          <div className='char-count text-gray-3 text-xs'>
            {t.LECTURE_PAGES.COMPONENTS.FLIP_CARD.EDIT.CHARACTER_COUNT(card.front.length, MAX_CHARS)}
          </div>
          <div className='dot dot-right d-flex align-items-center justify-content-center text-small bold' aria-hidden='true'>
            {index + 1}
          </div>
        </div>

        <div className='connector align-self-center' />

        <div className='pair-input d-flex flex-column'>
          <div className='dot dot-left d-flex align-items-center justify-content-center text-small bold' aria-hidden='true'>
            {index + 1}
          </div>
          <textarea
            ref={backRef}
            value={card.back}
            maxLength={MAX_CHARS}
            onChange={(e) => onChange('back', e.target.value)}
            onBlur={onBlur}
            placeholder={t.FORM.TYPE_HERE()}
            aria-label={t.LECTURE_PAGES.COMPONENTS.FLIP_CARD.EDIT.BACK()}
            data-qa={config.pendo.flipCard.editBack}
            data-qa-id={`${config.pendo.flipCard.editBack}_${card.id}`}
          />
          <div className='char-count text-gray-3 text-xs'>
            {t.LECTURE_PAGES.COMPONENTS.FLIP_CARD.EDIT.CHARACTER_COUNT(card.back.length, MAX_CHARS)}
          </div>
        </div>
      </div>

      <NvTooltip text={disableDelete ? t.LECTURE_PAGES.COMPONENTS.FLIP_CARD.EDIT.MIN_CARDS() : t.LECTURE_PAGES.COMPONENTS.FLIP_CARD.EDIT.REMOVE_CARD()}>
        <ClickableContainer
          className='delete-btn'
          onClick={onDelete}
          disabled={disableDelete}
          isFocusable={!disableDelete}
          aria-label={t.LECTURE_PAGES.COMPONENTS.FLIP_CARD.EDIT.REMOVE_CARD()}
          data-qa={config.pendo.flipCard.deleteCard}
          data-qa-id={`${config.pendo.flipCard.deleteCard}_${card.id}`}
        >
          <NvIcon
            icon='trash'
            size='small'
            className={`text-${disableDelete ? 'gray-5' : 'primary'}`}
          />
        </ClickableContainer>
      </NvTooltip>
    </div>
  );
};

export default FlipCardPairRow;
